'use client';

import { useTranslation } from 'next-i18next';
import { UserCircleIcon } from '@heroicons/react/24/outline';
import { Anchor, MissionControlSwitch } from '@components/common';
import { getI18nLinkHref } from '@services/i18nPaths';
import { slugify } from '@utils/strings/slugify';
import { useSidebarNavigation } from '@components/contexts/SidebarNavigationContext';
import type { JobSearchQueryParams } from '@type/filters';
import { MemoizedCompactJobAlertCard } from './CompactJobAlertCard';
import { POPULAR_SEARCH_KEYS } from './popularSearchKeys';

interface GuestJobsSidebarProps {
  locale: Locale;
  className?: string;
  onSwitchContext?: () => void;
  /** Opens the Jobs on Map modal. Provided by MissionControl to avoid import cycles. */
  onOpenMap?: (queryParams?: Partial<JobSearchQueryParams>) => void;
}

/**
 * @description Sidebar for unauthenticated (guest) users.
 * Shows a sign-in prompt followed by popular job search cards.
 * Each card links directly to the public job list for that keyword.
 */
export const GuestJobsSidebar = ({
  locale, className, onSwitchContext, onOpenMap,
}: GuestJobsSidebarProps) => {
  const { t } = useTranslation('common');
  const { navigateToEditSearch } = useSidebarNavigation();

  const handleSwitchContext = onSwitchContext ?? (() => {
    window.location.href = `/login?_target_path=${encodeURIComponent(`/${locale}/mc`)}`;
  });

  return (
    <div className={`flex h-full flex-col bg-surface ${className ?? ''}`}>
      <div className="scrollbar-hide flex-1 overflow-y-auto p-4">
        {/* Sign In prompt */}
        <div className="mb-6 flex flex-col gap-3 border-b border-input pb-4">
          <MissionControlSwitch
            activeContext="candidate"
            hireLabel={t('mc.switch.hire')}
            jobSearchLabel={t('mc.switch.job.search')}
            onSwitch={handleSwitchContext}
          />
          <Anchor
            href={`/login?_target_path=${encodeURIComponent(`/${locale}/mc`)}`}
            styling="none"
            className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-ink transition-colors hover:bg-surface-200"
          >
            <UserCircleIcon className="size-5 text-ink" />
            <span>{t('menu.sign_in-register') || 'Sign In'}</span>
          </Anchor>
        </div>

        {/* Popular Searches */}
        <div className="mb-3">
          <h3 className="mb-3 text-sm font-semibold text-ink">
            {t('user.popular.searches')}
          </h3>
          <div className="flex flex-col gap-3">
            {POPULAR_SEARCH_KEYS.slice(0, 6).map((key) => {
              const keyword = t(key);
              return (
                <MemoizedCompactJobAlertCard
                  key={key}
                  title={keyword}
                  locale={locale}
                  href={getI18nLinkHref(`/mc/job/list/${slugify(keyword)}`, locale)}
                  onEdit={() => navigateToEditSearch({ keyword })}
                  editHref={`${getI18nLinkHref(`/mc/job/list/${slugify(keyword)}`, locale)}?edit=1`}
                  onOpenMap={onOpenMap}
                />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
